import type { ReactElement, ReactNode } from 'react';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/components/ui/popover';
import { CopyToClipboardButton } from '@/components/shared/CopyToClipboardButton';
import { cn } from '@/lib/utils';

interface CopyTextPopoverProps {
  fullText: string;
  children: ReactNode;
  triggerClassName?: string;
  bodyClassName?: string;
  /**
   * Accessible name for the trigger button (exposed as `aria-label`). Set this
   * when the visible children are truncated so the full text stays discoverable
   * to screen readers. The native hover tooltip is owned by the caller's own
   * visible element (e.g. `MiddleTruncatedPath`'s text span), so this
   * intentionally does not set a `title` — keeping a single tooltip per cell.
   */
  triggerAriaLabel?: string;
  /**
   * When true, the trigger is fully inert: removed from the tab order
   * (`tabIndex=-1`), hidden from assistive tech (`aria-hidden`), and `disabled`.
   * `disabled` is what actually makes the `<button>` non-focusable — ARIA
   * forbids `aria-hidden` on a still-focusable element, so `tabIndex=-1` alone
   * (which only drops sequential tab order, not programmatic/`.focus()`
   * focusability) would leave a focusable aria-hidden control. Use this when the
   * affordance is visually inert (e.g. an untruncated path with nothing extra to
   * reveal) so keyboard/SR users don't land on an invisible control.
   */
  triggerInert?: boolean;
}

/**
 * Click-to-open popover with full text + copy button.
 * Uses internal button so PopoverTrigger asChild gets a proper ref.
 */
export function CopyTextPopover({
  fullText,
  children,
  triggerClassName,
  bodyClassName,
  triggerAriaLabel,
  triggerInert = false,
}: CopyTextPopoverProps): ReactElement {
  return (
    <Popover modal={false}>
      <PopoverTrigger asChild>
        <button
          type="button"
          aria-label={triggerInert ? undefined : triggerAriaLabel}
          aria-hidden={triggerInert || undefined}
          tabIndex={triggerInert ? -1 : undefined}
          disabled={triggerInert || undefined}
          className={cn(
            'inline-flex max-w-full min-w-0 items-center gap-1 text-left',
            triggerClassName
          )}
        >
          {children}
        </button>
      </PopoverTrigger>
      <PopoverContent
        className="z-[200] w-auto max-w-[min(90vw,32rem)] p-2"
        align="start"
        sideOffset={6}
        collisionPadding={8}
      >
        <div className="flex items-start gap-2">
          <p
            className={cn(
              'text-xs max-h-[min(40vh,320px)] overflow-y-auto break-words flex-1 whitespace-pre-wrap',
              bodyClassName
            )}
          >
            {fullText}
          </p>
          <CopyToClipboardButton
            text={fullText}
            compactCopiedState
            size="sm"
            variant="ghost"
            className="shrink-0 h-6 w-6 p-1"
          >
            <span className="sr-only">Copy</span>
          </CopyToClipboardButton>
        </div>
      </PopoverContent>
    </Popover>
  );
}

CopyTextPopover.displayName = 'CopyTextPopover';
